import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUpdateProfile, useUploadOwnPhoto, useGustoEnabled } from '../hooks';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageHeader } from '../components/page-header';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Upload, User, Briefcase, Users, Heart, Target, Lightbulb, Sparkles, GraduationCap, Link2 } from 'lucide-react';
import { ReportingChain } from '../components/people';
import { MyGustoInfoCard } from '../components/person';
import { dateToInputValue, dateToISOString, normalizeDateValue } from '../utils/formatters';

const MyProfile: React.FC = () => {
  const { t } = useTranslation('people');
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const updateProfile = useUpdateProfile();
  const uploadPhoto = useUploadOwnPhoto();
  const { isConnected: gustoConnected } = useGustoEnabled();

  const [formData, setFormData] = useState({
    birthDate: '',
    professionalDescription: '',
    professionalHabit: '',
    friendGroupKnownFor: '',
    hobbies: '',
    lifeGoal: '',
    funFact: '',
    careerLearning: '',
  });

  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        birthDate: dateToInputValue(user.birthDate),
        professionalDescription: user.professionalDescription || '',
        professionalHabit: user.professionalHabit || '',
        friendGroupKnownFor: user.friendGroupKnownFor || '',
        hobbies: user.hobbies || '',
        lifeGoal: user.lifeGoal || '',
        funFact: user.funFact || '',
        careerLearning: user.careerLearning || '',
      });
      if (user.profilePictureUrl || user.photoURL) {
        setPhotoPreview(user.profilePictureUrl || user.photoURL || null);
      }
    }
  }, [user]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Photo must be less than 5MB');
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const loading = updateProfile.isPending || uploadPhoto.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);

      // Upload photo if a new one was provided
      if (photo) {
        await uploadPhoto.mutateAsync(photo);
      }

      // Update profile data
      await updateProfile.mutateAsync({
        birthDate: formData.birthDate ? dateToISOString(formData.birthDate) : undefined,
        professionalDescription: formData.professionalDescription,
        professionalHabit: formData.professionalHabit,
        friendGroupKnownFor: formData.friendGroupKnownFor,
        hobbies: formData.hobbies,
        lifeGoal: formData.lifeGoal,
        funFact: formData.funFact,
        careerLearning: formData.careerLearning,
      });

      // Refresh user data
      await refreshUser();

      setSuccess(true);
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <PageHeader
        title="My Profile"
        description="Update your personal information and preferences"
        testId="my-profile-header"
      />

      {error && (
        <Alert variant="destructive" data-testid="my-profile-error-alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-500 bg-green-500/10" data-testid="my-profile-success-alert">
          <AlertDescription className="text-green-600">
            Profile updated successfully! Redirecting...
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="profile" className="space-y-6" data-testid="my-profile-tabs">
        <TabsList data-testid="my-profile-tabs-list">
          <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="my-profile-tab-profile">
            <User className="h-4 w-4" />
            {t('detail.tabs.profile')}
          </TabsTrigger>
          {gustoConnected && (
            <TabsTrigger value="gusto" className="flex items-center gap-2" data-testid="my-profile-tab-gusto">
              <Link2 className="h-4 w-4" />
              {t('detail.tabs.gusto')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" data-testid="my-profile-content-profile">
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="my-profile-form">
        {/* Avatar and Birth Date Card */}
        <Card data-testid="my-profile-basic-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Basic Information
            </CardTitle>
            <CardDescription>Your photo and birthday</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Profile Photo</Label>
              <div className="flex items-center gap-6">
                <Avatar className="h-24 w-24" data-testid="my-profile-avatar">
                  {photoPreview ? (
                    <AvatarImage src={photoPreview} alt={user.name} />
                  ) : (
                    <AvatarFallback>
                      <User className="h-12 w-12" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1">
                  <label
                    htmlFor="photo"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer transition-colors"
                    data-testid="my-profile-button-upload-photo"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Photo
                  </label>
                  <input
                    id="photo"
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    disabled={loading || success}
                    data-testid="my-profile-input-photo"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    PNG, JPG, WebP (max 5MB)
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthDate">Birth Date</Label>
              <Input
                id="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={(e) => setFormData({ ...formData, birthDate: normalizeDateValue(e.target.value) })}
                max={new Date().toISOString().split('T')[0]}
                disabled={loading || success}
                className="w-auto max-w-[200px]"
                data-testid="my-profile-input-birth-date"
              />
              <p className="text-xs text-muted-foreground">
                We'll use this to celebrate your birthday with the team
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Reporting Chain */}
        <ReportingChain 
          useCurrentUser 
          subject={{
            id: user.id,
            name: user.name,
            profilePictureUrl: user.profilePictureUrl,
            organizationRole: user.organizationRole,
          }}
        />

        {/* Professional Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Professional Information
            </CardTitle>
            <CardDescription>Tell us about your professional life</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="professionalDescription" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-blue-600" />
                How would you describe yourself professionally?
              </Label>
              <Textarea
                id="professionalDescription"
                value={formData.professionalDescription}
                onChange={(e) => setFormData({ ...formData, professionalDescription: e.target.value })}
                placeholder="e.g., I'm a creative problem solver who loves building scalable systems..."
                disabled={loading || success}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="professionalHabit" className="flex items-center gap-2">
                <Target className="h-4 w-4 text-purple-600" />
                What's a professional habit you're known for?
              </Label>
              <Textarea
                id="professionalHabit"
                value={formData.professionalHabit}
                onChange={(e) => setFormData({ ...formData, professionalHabit: e.target.value })}
                placeholder="e.g., Always asking 'why' to understand the root cause..."
                disabled={loading || success}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="careerLearning" className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-teal-600" />
                What are you currently learning or want to learn in your career?
              </Label>
              <Textarea
                id="careerLearning"
                value={formData.careerLearning}
                onChange={(e) => setFormData({ ...formData, careerLearning: e.target.value })}
                placeholder="e.g., Diving deep into machine learning and cloud architecture..."
                disabled={loading || success}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Personal Information
            </CardTitle>
            <CardDescription>Share a bit about yourself outside of work</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="friendGroupKnownFor" className="flex items-center gap-2">
                <Users className="h-4 w-4 text-green-600" />
                What are you known for among your friends?
              </Label>
              <Textarea
                id="friendGroupKnownFor"
                value={formData.friendGroupKnownFor}
                onChange={(e) => setFormData({ ...formData, friendGroupKnownFor: e.target.value })}
                placeholder="e.g., Being the one who always brings the best snacks..."
                disabled={loading || success}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hobbies" className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-pink-600" />
                What are your hobbies or interests?
              </Label>
              <Textarea
                id="hobbies"
                value={formData.hobbies}
                onChange={(e) => setFormData({ ...formData, hobbies: e.target.value })}
                placeholder="e.g., Photography, hiking, playing guitar..."
                disabled={loading || success}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lifeGoal" className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-600" />
                What's a life goal you're working towards?
              </Label>
              <Textarea
                id="lifeGoal"
                value={formData.lifeGoal}
                onChange={(e) => setFormData({ ...formData, lifeGoal: e.target.value })}
                placeholder="e.g., Travel to all 7 continents..."
                disabled={loading || success}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="funFact" className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-indigo-600" />
                Share a fun fact about yourself!
              </Label>
              <Textarea
                id="funFact"
                value={formData.funFact}
                onChange={(e) => setFormData({ ...formData, funFact: e.target.value })}
                placeholder="e.g., I can solve a Rubik's cube in under 2 minutes..."
                disabled={loading || success}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard')}
            disabled={loading || success}
            data-testid="my-profile-button-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || success}
            data-testid="my-profile-button-submit"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
        </TabsContent>

        {gustoConnected && (
          <TabsContent value="gusto" data-testid="my-profile-content-gusto">
            <MyGustoInfoCard />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default MyProfile;
